import { G, Svg } from '@svgdotjs/svg.js';
import { ClampJoint, CountertopAngleProcessingBase, CuttingMaterial, PostformingSideEnum } from 'cloud-shared-lib';
import { slotAppearance } from './countertop-render.utils';
import { PanelLengthChangeDirection } from './countertop.utils';
import { CountertopOutline } from './outline';

export enum PShapedCountertopSubtypeEnum {
  FullLengthTop,
  WithFullWidthSides,
  WithFullWidthRightSide,
  WithFullWidthLeftSide,
}

export enum GShapedCountertopSubtypeEnum {
  FullLengthTopWithRightSide,
  FullLengthTopWithLeftSide,
  WithFullWidthRightSide,
  WithFullWidthLeftSide,
}

export enum CountertopTypeEnum {
  Simple,
  PShaped,
  GShaped,
}

export type SlotAppearanceOverride = { slotId: number; appearance: typeof slotAppearance };

export interface RenderOptions {
  panelSizes?: string;
  linesJointSelectors?: boolean;
  sideSelectors?: boolean;
  disabledSideSelectors?: string[];
  angleSelectors?: boolean;
  panelSelectors?: boolean;
  panelSplit?: boolean;
  panelRemoving?: boolean;
  panelsJointsSelector?: boolean;
  highlightedSides?: { [key: string]: string };
  // TODO: Убрать. Вместо edgeBandingColors использовать highlightedSides
  edgeBandingColors?: { [key: string]: string };
  slotColors?: SlotAppearanceOverride[];
}

export interface Countertop {
  id: number;

  type: CountertopTypeEnum;
  subtype?: PShapedCountertopSubtypeEnum | GShapedCountertopSubtypeEnum;

  has3d: boolean;
  useClampJointForPanels: boolean;

  get material(): CountertopMaterial;
  set material(value: CountertopMaterial);

  get length(): number;
  set length(value: number);

  get width(): number;
  set width(value: number);

  lines: CountertopLine[];
  linesJoints?: CountertopLinesJoint[];

  count: number;

  outline: CountertopOutline;

  getAngleNeighbors(angleNumber: string):
    | {
        leftAngle?: string;
        rightAngle?: string;
        leftSide: string;
        rightSide: string;
      }
    | undefined;

  getProcessableNeighborAngles(angleNumber: string): {
    left?: CountertopAngleProcessingBase;
    right?: CountertopAngleProcessingBase;
  };

  setLineWidth(lineId: string, width: number): void;

  checkSidePostforming(sideN: string): boolean;

  setPanelLength(value: number, lineUid: string, panelUid: string, direction: PanelLengthChangeDirection);
  deletePanel(lineUid: string, panelUid: string): number;

  canSetWidth(width: number): boolean;
  canSetLength(width: number): boolean;

  render(
    draw: Svg,
    scaled,
    onPanelSizeChange: (
      countertop: Countertop,
      line: CountertopLine,
      panel: CountertopPanel,
      side: 'height' | 'width',
      value: number,
      error?: string
    ) => void,
    onPanelCountChange: (
      countertop: Countertop,
      line: CountertopLine,
      panel: CountertopPanel,
      mode: 'add' | 'delete'
    ) => void,
    onAngleSelectorClick: (angleNumber: number, angleProcessing: CountertopAngleProcessingBase) => void,
    onSideSelectorClick: (sideNumber: number) => void,
    onLinesJointSelectorClick?: (joint: CountertopLinesJoint) => void,
    onClampJointClick?: (joint: ClampJoint) => void,
    options?: RenderOptions
  ): Promise<G[]>;
  renderTextureSchema(draw: Svg): Promise<G[]>;
  getLineByAngleNumber(angleNumber: number): CountertopLine;
  getSideLengthByNumber(n: string): number;
  getNeighborsByAxis(angleNumber: string, axis: 'x' | 'y'): { angle: string; side: string };
}

export interface CountertopMaterial extends CuttingMaterial {
  postforming: PostformingSideEnum;
}

export interface CountertopLine {
  uid: string;
  material: CountertopMaterial;
  panels: CountertopPanel[];
  panelsJoints: CountertopPanelsJoint[];
  isVertical: boolean;

  get length(): number;
  set length(value: number);

  get width(): number;
  set width(value: number);

  addPanel(height?: number, panelId?: string, direction?: 'after' | 'before'): CountertopPanel;
}

export interface CountertopPanel {
  uid: string;

  length: number;
  width: number;
}

export interface CountertopPanelsJoint {
  panel1: CountertopPanel;
  panel2: CountertopPanel;
  connector: ClampJoint | null;
}

export interface CountertopLinesJoint {
  maleUid: string;
  femaleUid: string;
  type: CountertopLinesJointTypeEnum;
  connector: ClampJoint | null;
  params: MasonsMitreJointParams | null;
}

export interface MasonsMitreJointParams {
  radius: number;
  allowance: number;
}

export enum CountertopLinesJointTypeEnum {
  None,
  MasonsMitre,
  Clamp,
}
